import React, { Fragment, forwardRef } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import InputMask from 'sb-text-mask'
import { useUniqueId } from 'sb-hooks'
import { intlMessageShape, useIntl, Message } from 'sb-react-intl'

import { Icon, CircleLoader } from 'components/_new/ui'
import VisuallyHidden from 'components/ui/VisuallyHidden/VisuallyHidden'
import { FieldError, AutosizeTextarea } from 'components/_new/forms'

import messages from './messages'
import s from './StandardInputView.scss'

import eraseImage from './images/erase.svg'


const StandardInputView = forwardRef((props, ref) => {
  const {
    className, type, placeholder, label, value, error, mask, maskChar, maskGuide, submitButtonMessage,
    readOnly, errored, loading, withBorder, withEraser, beforeNode, afterNode,
    icon, iconColor, iconButtonProps,
    loaderClassName, inputClassName,
    onKeyPress, onChange, onBlur, onFocus, onErase, onSubmit, onTouchStart,
    onBeforeNodeClick, onAfterNodeClick, onIconClick,
    dataRecurly, dataTestId, dataId, id, rows, autosize,
    autoComplete, eraserLabel, semiboldPlaceholder,
  } = props

  const intl        = useIntl()
  const isTextArea  = Boolean(rows)
  const uniqueId    = useUniqueId()
  const inputId     = id || `input_${uniqueId}` // for "htmlFor" attribute

  const contentClassName = cx(s.content, className, {
    [s.errored]: errored || error,
    [s.bordered]: withBorder,
    [s.disabled]: readOnly,
  })

  const _inputClassName = cx(s.input, inputClassName, {
    [s.withoutBeforeNode]: !beforeNode,
    [s.textArea]: autosize || isTextArea,
    [s.semiboldPlaceholder]: semiboldPlaceholder,
  })

  const nodeProps = {
    id: inputId,
    className: _inputClassName,
    type,
    value,
    placeholder: placeholder ? intl.message(placeholder) : undefined,
    readOnly,
    disabled: readOnly,
    tabIndex: readOnly ? -1 : 0,
    autoComplete,
    onKeyPress,
    onChange,
    onBlur,
    onFocus,
    onTouchStart,
    'data-testid': dataTestId,
    'data-recurly': dataRecurly,
    'data-id': dataId,
  }

  let component = 'input'

  if (mask) {
    nodeProps.forwardedRef = ref
    nodeProps.mask = mask
    nodeProps.guide = maskGuide
    nodeProps.placeholderChar = maskChar

    component = InputMask
  }
  if (autosize) {
    nodeProps.ref = ref
    nodeProps.rows = rows
    delete nodeProps.type

    component = AutosizeTextarea
  }
  else if (isTextArea) {
    nodeProps.rows = rows
    nodeProps.ref  = ref

    component = 'textarea'
  }
  else {
    nodeProps.ref = ref
  }

  return (
    <Fragment>
      <div className={contentClassName}>
        {
          Boolean(label || placeholder) && (
            <VisuallyHidden>
              <Message text={label || placeholder} tag="label" htmlFor={inputId} />
            </VisuallyHidden>
          )
        }
        {
          Boolean(beforeNode) && (
            <div className={s.beforeContainer} role={onBeforeNodeClick ? 'button' : null} onClick={onBeforeNodeClick}>
              {beforeNode}
            </div>
          )
        }
        {
          React.createElement(component, nodeProps)
        }
        {
          Boolean(!loading && typeof onSubmit === 'function') && (
            <button
              className={s.submitButton}
              type="button"
              data-testid={`${dataTestId}SubmitButton`}
              onClick={onSubmit}
            >
              <Message text={submitButtonMessage || messages.submitButton} />
            </button>
          )
        }
        {
          Boolean(!loading && withEraser && value) && (
            <button
              className={s.eraser}
              type="button"
              aria-label={eraserLabel}
              data-testid={`${dataTestId}EraseButton`}
              onClick={onErase}
            >
              <img src={eraseImage} alt="" />
            </button>
          )
        }
        {
          Boolean(!loading && icon) && (
            <button
              type="button"
              {...iconButtonProps}
              className={cx(s.iconButton, iconButtonProps.className)}
              data-testid={`${dataTestId}Button`}
              onClick={onIconClick}
            >
              <Icon className={s.icon} name={icon} color={iconColor} noWrapper />
            </button>
          )
        }
        {
          Boolean(loading) && (
            <div className={s.spinner}>
              <CircleLoader className={loaderClassName} />
            </div>
          )
        }
        {
          Boolean(afterNode) && (
            <div className={s.afterContainer} role={onAfterNodeClick ? 'button' : null} onClick={onAfterNodeClick}>
              {afterNode}
            </div>
          )
        }
      </div>
      {
        Boolean(error) && (
          <FieldError message={error} dataTestId={`${dataTestId}Error`} />
        )
      }
    </Fragment>
  )
})

StandardInputView.displayName = 'StandardInputView'

StandardInputView.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  value: PropTypes.string,
  rows: PropTypes.number,
  autosize: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  iconButtonProps: PropTypes.object,
  maskChar: PropTypes.string,
  maskGuide: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  withBorder: PropTypes.bool,
  withEraser: PropTypes.bool,
  semiboldPlaceholder: PropTypes.bool,
  beforeNode: PropTypes.node,
  afterNode: PropTypes.node,
  loaderClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onErase: PropTypes.func,
  onSubmit: PropTypes.func,
  onIconClick: PropTypes.func,
  onTouchStart: PropTypes.func,
  onBeforeNodeClick: PropTypes.func,
  onAfterNodeClick: PropTypes.func,
  dataTestId: PropTypes.string,
  dataRecurly: PropTypes.string,
  // for search
  dataId: PropTypes.string,
}

StandardInputView.defaultProps = {
  maskGuide: false,
  withBorder: true,
  iconButtonProps: {},
}


export default StandardInputView
